import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Clock, Play, Plus, Upload, UserPlus } from 'lucide-react';

import { listEmployees, type EmployeeListItem } from '@/api/employees.api';
import {
  addRosterEntry,
  getLedgerWeek,
  listRoster,
  runEvaluationNow,
  updateRosterEntry,
  uploadLedger,
  type EvaluationSummary,
  type LedgerWeekRow,
  type RosterRow,
  type UploadLedgerEntry,
} from '@/api/overtime.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';
import { hasCapability, isSuperadmin } from '@/lib/capabilities';

/**
 * Overtime Tracker — Phase 14's admin surface, shipped in post-audit
 * Tier 2 (the audit found the entire UI missing: the pipeline could
 * compute but nothing could feed or read it).
 *
 * Three areas on one page:
 *   1. Week table — every hourly roster member × Mon-Fri hours for
 *      the picked week, with weekly totals color-hinted vs the 40h cap
 *   2. Roster management — add/deactivate hourly employees, set the
 *      manager whose alerts should mention them
 *   3. Actions — upload hours (JSON paste, V1's Gusto-CSV fallback
 *      per the plan) + "Run evaluation now" (superadmin)
 *
 * Access mirrors the server gates: view needs `view_alert_dispatches`,
 * upload needs `upload_time_data`, roster needs `manage_alerts`,
 * run-now is superadmin-only. Superadmins hold everything implicitly.
 */

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function OvertimeTrackerPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();

  const access = useMemo(() => {
    if (authState.status !== 'authenticated') {
      return { view: false, upload: false, roster: false, runNow: false };
    }
    const me = authState.me;
    return {
      view: hasCapability(me, 'view_alert_dispatches'),
      upload: hasCapability(me, 'upload_time_data'),
      roster: hasCapability(me, 'manage_alerts'),
      runNow: isSuperadmin(me),
    };
  }, [authState]);

  const [weekOf, setWeekOf] = useState(currentMonday);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [lastRun, setLastRun] = useState<EvaluationSummary | null>(null);

  const ledgerQuery = useQuery({
    queryKey: ['overtime-ledger', weekOf],
    queryFn: () => getLedgerWeek(weekOf),
    enabled: access.view,
  });

  const rosterQuery = useQuery({
    queryKey: ['overtime-roster'],
    queryFn: listRoster,
    enabled: access.roster,
  });

  const runNowMutation = useMutation({
    mutationFn: runEvaluationNow,
    onSuccess: (summary) => {
      setLastRun(summary);
    },
  });

  function invalidateLedger(): void {
    void queryClient.invalidateQueries({ queryKey: ['overtime-ledger'] });
  }
  function invalidateRoster(): void {
    void queryClient.invalidateQueries({ queryKey: ['overtime-roster'] });
    invalidateLedger();
  }

  if (!access.view) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          Back to dashboard
        </Link>
        <Card className="flex items-start gap-3 p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-warning-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-shark">Access required</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              The overtime tracker is visible to visionary and integrator roles, and to members granted the
              view-alert-dispatches capability.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const rows = ledgerQuery.data?.rows ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1 text-[12px] text-kw-muted hover:text-kw-shark"
          >
            <ArrowLeft className="size-3.5" />
            Back to dashboard
          </Link>
          <h1 className="typo-h1 mt-1">Overtime Tracker</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            Weekly clocked hours per hourly employee. The daily evaluator (Tue–Fri 06:30 ET) reads this ledger
            and emails trim/breach alerts to subscribers.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {access.upload ? (
            <Button
              variant="secondary"
              onClick={() => {
                setUploadOpen(true);
              }}
            >
              <Upload className="size-4" />
              Upload hours
            </Button>
          ) : null}
          {access.runNow ? (
            <Button
              onClick={() => {
                runNowMutation.mutate();
              }}
              isLoading={runNowMutation.isPending}
              title="Re-runs today's evaluation. Note: pressing after the morning cron already ran sends duplicate alerts (V1 has no same-day dedup)."
            >
              <Play className="size-4" />
              Run evaluation now
            </Button>
          ) : null}
        </div>
      </div>

      {lastRun ? (
        <Card className="flex items-start gap-3 border-kw-tag-text/30 bg-kw-emerald-light p-4">
          <Clock className="mt-0.5 size-5 shrink-0 text-kw-tag-text" />
          <p className="text-[13px] text-kw-tag-text">
            Evaluation for {lastRun.evaluatedFor}: {lastRun.employeesEvaluated} evaluated,{' '}
            {lastRun.eventsCreated} alerts, {lastRun.mailsSent} emails sent
            {lastRun.mailsFailed > 0 ? `, ${String(lastRun.mailsFailed)} failed` : ''}
            {lastRun.errors.length > 0
              ? ` — ${String(lastRun.errors.length)} employee(s) skipped on error`
              : ''}
            .
          </p>
        </Card>
      ) : null}
      {runNowMutation.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <p className="text-[13px] text-kw-danger-text">
            {runNowMutation.error instanceof Error ? runNowMutation.error.message : 'Evaluation failed'}
          </p>
        </Card>
      ) : null}

      <div className="flex items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Week of (Monday)</span>
          <input
            type="date"
            value={weekOf}
            onChange={(e) => {
              setWeekOf(e.target.value);
            }}
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </label>
        {ledgerQuery.data ? (
          <span className="pb-2 text-[12px] text-kw-muted">Showing week of {ledgerQuery.data.weekOf}</span>
        ) : null}
      </div>

      {ledgerQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : ledgerQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <p className="text-[13px] text-kw-danger-text">
            {ledgerQuery.error instanceof Error ? ledgerQuery.error.message : 'Could not load the ledger'}
          </p>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Clock className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No hourly employees on the roster yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Add your first hourly employee below to enable the tracker.
            </p>
          </div>
          {access.roster ? (
            <Button
              onClick={() => {
                setAddOpen(true);
              }}
            >
              <UserPlus className="size-4" />
              Add hourly employee
            </Button>
          ) : null}
        </Card>
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="border-b border-kw-border bg-kw-bg/40 text-left text-[11px] uppercase tracking-wide text-kw-muted">
                <th className="px-4 py-2">Employee</th>
                {DAY_LABELS.map((day) => (
                  <th key={day} className="px-3 py-2 text-right">
                    {day}
                  </th>
                ))}
                <th className="px-4 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kw-border">
              {rows.map((row) => (
                <LedgerRow key={row.employeeMembershipId} row={row} />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {access.roster ? (
        <RosterSection
          roster={rosterQuery.data ?? []}
          loading={rosterQuery.isLoading}
          onAdd={() => {
            setAddOpen(true);
          }}
          onChanged={invalidateRoster}
        />
      ) : null}

      <UploadHoursModal
        open={uploadOpen}
        defaultWeekOf={weekOf}
        onClose={() => {
          setUploadOpen(false);
        }}
        onUploaded={invalidateLedger}
      />
      <AddRosterModal
        open={addOpen}
        existingMembershipIds={new Set((rosterQuery.data ?? []).map((r) => r.employeeMembershipId))}
        onClose={() => {
          setAddOpen(false);
        }}
        onAdded={invalidateRoster}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Ledger row
// ─────────────────────────────────────────────────────────────────────────

function LedgerRow({ row }: { row: LedgerWeekRow }) {
  // Color hint against the 40h federal cap: yellow within 15 min of
  // pace-to-40, red at/over 40. Mirrors the evaluator's thresholds
  // loosely — display-only, the evaluator owns the real rule.
  const totalClass =
    row.weekTotal >= 40
      ? 'text-kw-danger-text font-semibold'
      : row.weekTotal >= 38
        ? 'text-kw-warning-text font-medium'
        : 'text-kw-shark';
  return (
    <tr className={row.isActive ? 'text-kw-shark' : 'text-kw-muted'}>
      <td className="px-4 py-2 font-medium">
        {row.employeeName}
        {!row.isActive ? <span className="ml-1 text-[11px] text-kw-muted">(inactive)</span> : null}
      </td>
      {row.hoursByDay.map((hours, idx) => (
        <td key={idx} className="px-3 py-2 text-right tabular-nums">
          {hours === 0 ? <span className="text-kw-ghost">—</span> : hours.toFixed(2)}
        </td>
      ))}
      <td className={`px-4 py-2 text-right tabular-nums ${totalClass}`}>{row.weekTotal.toFixed(2)}</td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Roster section
// ─────────────────────────────────────────────────────────────────────────

function RosterSection({
  roster,
  loading,
  onAdd,
  onChanged,
}: {
  roster: RosterRow[];
  loading: boolean;
  onAdd: () => void;
  onChanged: () => void;
}) {
  const toggleMutation = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      updateRosterEntry(input.id, { isActive: input.isActive }),
    onSuccess: onChanged,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-[16px] font-semibold text-kw-shark">Hourly roster</h2>
          <p className="text-[13px] text-kw-muted">
            Who the daily evaluator watches. Deactivating keeps history but stops evaluations.
          </p>
        </div>
        <Button variant="secondary" onClick={onAdd}>
          <Plus className="size-4" />
          Add
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="size-6 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : roster.length === 0 ? null : (
        <Card className="p-0">
          <ul className="divide-y divide-kw-border">
            {roster.map((entry) => (
              <li key={entry.id} className="flex items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-medium text-kw-shark">{entry.employeeName}</p>
                  <p className="text-[12px] text-kw-muted">
                    {entry.employeeEmail}
                    {entry.managerName ? ` · manager: ${entry.managerName}` : ' · no manager set'}
                    {entry.state ? ` · ${entry.state}` : ''}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  isLoading={toggleMutation.isPending}
                  onClick={() => {
                    toggleMutation.mutate({ id: entry.id, isActive: !entry.isActive });
                  }}
                >
                  {entry.isActive ? 'Deactivate' : 'Reactivate'}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Upload hours modal — V1 JSON paste (the plan's Gusto-CSV fallback)
// ─────────────────────────────────────────────────────────────────────────

function UploadHoursModal({
  open,
  defaultWeekOf,
  onClose,
  onUploaded,
}: {
  open: boolean;
  defaultWeekOf: string;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [raw, setRaw] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (entries: UploadLedgerEntry[]) => uploadLedger(entries),
    onSuccess: () => {
      onUploaded();
      onClose();
      setRaw('');
      setParseError(null);
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setParseError(null);
    let entries: UploadLedgerEntry[];
    try {
      const parsed: unknown = JSON.parse(raw);
      const list = Array.isArray(parsed)
        ? parsed
        : typeof parsed === 'object' &&
            parsed !== null &&
            Array.isArray((parsed as { entries?: unknown }).entries)
          ? (parsed as { entries: unknown[] }).entries
          : null;
      if (!list) throw new Error('expected a JSON array of entries, or { "entries": [...] }');
      entries = list as UploadLedgerEntry[];
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Invalid JSON');
      return;
    }
    mutation.mutate(entries);
  }

  const placeholder = JSON.stringify(
    [
      {
        employeeMembershipId: 'mem_...',
        workweekStartDate: defaultWeekOf,
        hoursByDay: [8, 8.5, 9, 0, 0, 0, 0],
      },
    ],
    null,
    2,
  );

  return (
    <Modal open={open} onClose={onClose} title="Upload hours (JSON)" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-[13px] text-kw-muted">
          Paste the weekly hours export. One entry per employee per week; <code>hoursByDay</code> is Mon→Sun.
          Re-uploading the same week overwrites — safe to correct mistakes. (Direct Gusto CSV import lands
          once a sample report is available.)
        </p>
        <textarea
          value={raw}
          onChange={(e) => {
            setRaw(e.target.value);
          }}
          rows={10}
          placeholder={placeholder}
          className="w-full rounded-kw-md border border-kw-border bg-white p-3 font-mono text-[12px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
        />
        {parseError ? <p className="text-[12px] text-kw-danger-text">JSON error: {parseError}</p> : null}
        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {mutation.error instanceof Error ? mutation.error.message : 'Upload failed'}
            </p>
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={raw.trim() === ''}>
            <Upload className="size-4" />
            Upload
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Add roster modal
// ─────────────────────────────────────────────────────────────────────────

function AddRosterModal({
  open,
  existingMembershipIds,
  onClose,
  onAdded,
}: {
  open: boolean;
  existingMembershipIds: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [employeeMembershipId, setEmployeeMembershipId] = useState('');
  const [managerMembershipId, setManagerMembershipId] = useState('');
  const [state, setState] = useState('');

  const employeesQuery = useQuery({
    queryKey: ['employees', 'all'],
    queryFn: () => listEmployees({ status: 'all', limit: 500 }),
    enabled: open,
  });

  const withMembership = useMemo(
    () => (employeesQuery.data?.data ?? []).filter((e: EmployeeListItem) => e.membershipId),
    [employeesQuery.data],
  );

  const mutation = useMutation({
    mutationFn: () =>
      addRosterEntry({
        employeeMembershipId,
        managerMembershipId: managerMembershipId || null,
        state: state.trim() || null,
      }),
    onSuccess: () => {
      onAdded();
      onClose();
      setEmployeeMembershipId('');
      setManagerMembershipId('');
      setState('');
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!employeeMembershipId) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Add hourly employee" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Employee</span>
          <select
            required
            value={employeeMembershipId}
            onChange={(e) => {
              setEmployeeMembershipId(e.target.value);
            }}
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          >
            <option value="">Pick an employee…</option>
            {withMembership.map((emp) => (
              <option
                key={emp.membershipId}
                value={emp.membershipId ?? ''}
                disabled={existingMembershipIds.has(emp.membershipId ?? '')}
              >
                {emp.firstName} {emp.lastName}
                {existingMembershipIds.has(emp.membershipId ?? '') ? ' (already on roster)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Manager (optional)</span>
          <select
            value={managerMembershipId}
            onChange={(e) => {
              setManagerMembershipId(e.target.value);
            }}
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          >
            <option value="">No manager routing</option>
            {withMembership.map((emp) => (
              <option key={emp.membershipId} value={emp.membershipId ?? ''}>
                {emp.firstName} {emp.lastName}
              </option>
            ))}
          </select>
        </label>
        <Input
          label="State (optional)"
          name="state"
          value={state}
          onChange={(e) => {
            setState(e.target.value);
          }}
          placeholder="e.g. FL"
          hint="Documentation only in V1 — federal 40h applies uniformly."
        />
        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {mutation.error instanceof Error ? mutation.error.message : 'Could not add'}
            </p>
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!employeeMembershipId}>
            Add to roster
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

/** Monday of the current week, YYYY-MM-DD (local calendar). */
function currentMonday(): string {
  const now = new Date();
  const dayOfWorkweek = (now.getDay() + 6) % 7; // 0=Mon..6=Sun
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWorkweek);
  const yyyy = monday.getFullYear();
  const mm = String(monday.getMonth() + 1).padStart(2, '0');
  const dd = String(monday.getDate()).padStart(2, '0');
  return `${String(yyyy)}-${mm}-${dd}`;
}
